/**
 * F00120 24-hour flow sheet helpers (Phase 8 §3.6c/e).
 *
 * Grid shape (as stored on the NURSING_ADDENDUM form): `{ [time "HH:MM"]: { [day "Sun".."Sat"]: { care, caregiver } } }`.
 * Each cell is a 15-minute slot, so a coded cell counts 0.25 h for its caregiver code.
 */

export const FLOWSHEET_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export type FlowsheetDay = (typeof FLOWSHEET_DAYS)[number];

export const CAREGIVER_CODES = ['N', 'O', 'P', 'Q', 'S'] as const;
export type CaregiverCode = (typeof CAREGIVER_CODES)[number];

/**
 * Short labels for the F00120 Care Giver Codes ("N=PDN hours, O=other in-home resource(s),
 * P=family (if family has volunteered), Q=PPECC hours, S=school/daycare"). Each code has its own
 * total: Q (PPECC) is never counted as family (P) hours.
 */
export const CAREGIVER_LABELS: Record<CaregiverCode, string> = {
  N: 'PDN',
  O: 'Other in-home resource',
  P: 'Family',
  Q: 'PPECC',
  S: 'School/daycare',
};

export const SLOT_HOURS = 0.25;

export interface FlowsheetCell {
  care: string;
  caregiver: string;
}

export type FlowsheetGrid = Record<string, Record<string, FlowsheetCell>>;

/** 96 slots "00:00" … "23:45". */
export const FLOWSHEET_TIME_SLOTS: string[] = (() => {
  const slots: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return slots;
})();

export function buildEmptyFlowsheetGrid(): FlowsheetGrid {
  const grid: FlowsheetGrid = {};
  FLOWSHEET_TIME_SLOTS.forEach(slot => {
    grid[slot] = {};
    FLOWSHEET_DAYS.forEach(day => {
      grid[slot][day] = { care: '', caregiver: '' };
    });
  });
  return grid;
}

export interface FlowsheetTotals {
  /** Weekly hours per caregiver code. */
  byCode: Record<CaregiverCode, number>;
  /** Weekly hours across all codes. */
  totalHours: number;
}

function isCaregiverCode(value: string): value is CaregiverCode {
  return (CAREGIVER_CODES as readonly string[]).includes(value);
}

/** Read a grid from untyped form data; anything malformed is ignored. */
export function asFlowsheetGrid(value: unknown): FlowsheetGrid {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as FlowsheetGrid;
}

export function computeFlowsheetTotals(grid: FlowsheetGrid | null | undefined): FlowsheetTotals {
  const byCode: Record<CaregiverCode, number> = { N: 0, O: 0, P: 0, Q: 0, S: 0 };
  Object.values(grid || {}).forEach(days => {
    if (!days || typeof days !== 'object') return;
    Object.values(days).forEach(cell => {
      const code = (cell?.caregiver || '').trim().toUpperCase();
      if (isCaregiverCode(code)) byCode[code] += SLOT_HOURS;
    });
  });
  const totalHours = CAREGIVER_CODES.reduce((sum, c) => sum + byCode[c], 0);
  return { byCode, totalHours };
}

/** True when no cell has care text or a caregiver code. */
export function isFlowsheetEmpty(grid: FlowsheetGrid | null | undefined): boolean {
  return !Object.values(grid || {}).some(days =>
    days && typeof days === 'object' && Object.values(days).some(cell => !!(cell?.care || '').trim() || !!(cell?.caregiver || '').trim())
  );
}

/**
 * Convert a PA per-week quantity to hours using the PA unit config.
 * Returns null when the unit can't be expressed in hours (visits, or units without minutes).
 */
export function perWeekToHours(
  perWeek: number | null | undefined,
  unitType: string | null | undefined,
  minutesPerUnit: number | null | undefined
): number | null {
  if (perWeek === null || perWeek === undefined || !Number.isFinite(perWeek)) return null;
  if (unitType === 'hours') return perWeek;
  if (unitType === 'units' && minutesPerUnit && minutesPerUnit > 0) return (perWeek * minutesPerUnit) / 60;
  return null;
}

/** Parse a numeric form field ("40", 40, "") into a number or null. */
export function parseHours(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Hours compare equal when within one 15-minute slot's rounding. */
export function hoursMatch(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.01;
}

export function formatHours(hours: number): string {
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(2).replace(/0$/, '');
}

export interface PrefillOptions {
  /** Start of the daily shift, "HH:MM" on a 15-minute boundary. Default 07:00 (19:00 with night coverage). */
  startTime?: string;
  nightCoverage?: boolean;
}

/** Days filled for N days/week: Mon first, weekend last. */
const PREFILL_DAY_ORDER: FlowsheetDay[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Build a grid with caregiver code N for `hoursPerDay` on `daysPerWeek` days (care-plan prefill, §3.6e).
 * Slots past midnight wrap to the start of the same day column (the sheet is a weekly template).
 * Care text is left empty; cells stay editable.
 */
export function buildPrefilledFlowsheet(
  hoursPerDay: number,
  daysPerWeek: number,
  options: PrefillOptions = {}
): FlowsheetGrid {
  const grid = buildEmptyFlowsheetGrid();
  const slotsPerDay = Math.max(0, Math.min(96, Math.round(hoursPerDay * 4)));
  const dayCount = Math.max(0, Math.min(7, Math.round(daysPerWeek)));
  const defaultStart = options.nightCoverage ? '19:00' : '07:00';
  const startIndex = Math.max(0, FLOWSHEET_TIME_SLOTS.indexOf(options.startTime || defaultStart));
  const days = PREFILL_DAY_ORDER.slice(0, dayCount);
  days.forEach(day => {
    for (let i = 0; i < slotsPerDay; i++) {
      const slot = FLOWSHEET_TIME_SLOTS[(startIndex + i) % 96];
      grid[slot][day] = { care: '', caregiver: 'N' };
    }
  });
  return grid;
}

export interface PdnFrequency {
  hoursPerDay: number;
  daysPerWeek: number;
  nightCoverage: boolean;
}

/**
 * RN care-plan "Skilled Nursing Hours" section → hours/day, days/week.
 * The form stores `pdnHoursPerDay`/`pdnDaysPerWeek`/`pdnNightCoverageRequired`; the DTO names
 * (`pdnHours_hoursPerDay`, …) are accepted too. Null when either number is missing.
 */
export function extractPdnFrequency(data: Record<string, unknown> | null | undefined): PdnFrequency | null {
  if (!data) return null;
  const hoursPerDay = parseHours(data.pdnHoursPerDay ?? data.pdnHours_hoursPerDay);
  const daysPerWeek = parseHours(data.pdnDaysPerWeek ?? data.pdnHours_daysPerWeek);
  if (!hoursPerDay || !daysPerWeek || hoursPerDay <= 0 || daysPerWeek <= 0) return null;
  const night = data.pdnNightCoverageRequired ?? data.pdnHours_nightCoverage;
  return { hoursPerDay: Math.min(24, hoursPerDay), daysPerWeek: Math.min(7, daysPerWeek), nightCoverage: night === true || night === 'true' };
}
